/**
 * Таблица дел и загрузка её строк.
 *
 * Пагинация серверная: `GET /api/cases?page&limit` (хук `useCases`). Исключение —
 * чип «Повторные»: у `GET /api/cases` нет параметра для `repeat_of`, поэтому такие
 * дела берём готовой выборкой `GET /api/dashboard/list?kpi=repeat` (до 200 строк)
 * и режем на страницы уже на клиенте, применяя к ним те же фильтры и ту же
 * сортировку. Если сервер обрезал выборку лимитом, `truncated` поднят и экран
 * обязан сказать об этом вслух: иначе «показано 1–25 из 200» молча скроет остаток.
 *
 * `variant="my"` — семь колонок макета (`.my-row`), `variant="full"` — девять колонок
 * ТЗ §8.4 на `.list-row.cases-row`: сетку колонок задаёт добавление в `styles/index.css`,
 * без него строка остаётся гибкой строкой списка из макета и всё равно читается.
 */
import type { ReactNode } from 'react';
import { categoryLabel } from '@/lib/categories';
import type { ApiError } from '@/api/client';
import { useCases, useDashboardList } from '@/api/hooks';
import type { CaseListItem, CaseListParams } from '@/api/types';
import type { CaseRowVariant } from './CaseRow';
import { CaseRow } from './CaseRow';
import type { FilterState } from './CaseFilters';
import { chipParams } from './CaseFilters';
import { SkeletonRows } from '../ErrorState';

export type CaseSort = NonNullable<CaseListParams['sort']>;

export const CASE_SORTS: readonly CaseSort[] = ['deadline', 'created', 'updated', 'priority'];

export function isCaseSort(value: string | null | undefined): value is CaseSort {
    return !!value && (CASE_SORTS as readonly string[]).includes(value);
}

export interface CaseQuery extends FilterState {
    page: number;
    limit: number;
    sort: CaseSort;
}

/** Собирает параметры запроса: сперва то, что задал чип, поверх — выбранное в селектах. */
export function caseListParams(query: CaseQuery): CaseListParams {
    const params: CaseListParams = { ...chipParams(query.chip) };
    if (query.stage) params.stage = query.stage;
    if (query.category) params.category = query.category;
    if (query.priority) params.priority = Number(query.priority);
    if (query.executor) params.executor = query.executor;
    if (query.department) params.department = query.department;
    if (query.source) params.source = query.source;
    if (query.deadline) params.deadline = query.deadline as CaseListParams['deadline'];
    const needle = query.q.trim();
    if (needle) params.q = needle;
    params.page = query.page;
    params.limit = query.limit;
    params.sort = query.sort;
    return params;
}

/** Локальный отбор для чипа «Повторные» — повторяет предикаты серверных фильтров. */
function matchesQuery(item: CaseListItem, query: CaseQuery): boolean {
    if (query.stage && item.stage !== query.stage) return false;
    if (query.category && item.primary_category !== query.category) return false;
    if (query.priority && String(item.priority) !== query.priority) return false;

    if (query.executor === 'none') {
        if (item.executor_id) return false;
    } else if (query.executor && item.executor_id !== query.executor) {
        return false;
    }

    if (query.department === 'none') {
        if (item.department_id) return false;
    } else if (query.department && item.department_id !== query.department) {
        return false;
    }

    if (query.source && item.source !== query.source) return false;

    if (query.deadline) {
        const left = item.days_left;
        if (typeof left !== 'number') return false;
        if (query.deadline === 'late' && !(left < 0)) return false;
        if (query.deadline === 'soon' && !(left >= 0 && left <= 3)) return false;
        if (query.deadline === 'today' && left !== 0) return false;
        // Границы те же, что в SQL `GET /api/cases` (`BETWEEN 0 AND 7`).
        if (query.deadline === 'week' && !(left >= 0 && left <= 7)) return false;
        if (query.deadline === 'ok' && !(left > 3)) return false;
    }

    const needle = query.q.trim().toLowerCase();
    if (needle) {
        const haystack = [item.number, item.title, item.applicant, item.primary_category, categoryLabel(item.primary_category), item.executor_name]
            .filter((value): value is string => typeof value === 'string' && value.length > 0)
            .join(' ')
            .toLowerCase();
        if (!haystack.includes(needle)) return false;
    }
    return true;
}

/** Дата поступления: та же `COALESCE(received_at, created_at)`, что в SQL списка. */
function regDate(item: CaseListItem): string {
    return item.received_at || item.created_at || '';
}

/**
 * Сортировка выборки «Повторные» на клиенте: `GET /api/dashboard/list` порядок не
 * принимает и всегда отдаёт по сроку ответа, а селект «Сортировка» есть и должен
 * работать. Порядок полей повторяет `ORDER_BY` бэкенда (`routes/cases.ts`).
 */
function sortItems(items: CaseListItem[], sort: CaseSort): CaseListItem[] {
    const byDeadline = (a: CaseListItem, b: CaseListItem): number => {
        const ad = a.legal_deadline ?? '';
        const bd = b.legal_deadline ?? '';
        // NULLS LAST: дело без срока уходит в конец.
        if (ad === '' || bd === '') {
            if (ad !== bd) return ad === '' ? 1 : -1;
        } else if (ad !== bd) {
            return ad.localeCompare(bd);
        }
        const ar = regDate(a);
        const br = regDate(b);
        if (ar !== br) return ar.localeCompare(br);
        return a.case_id.localeCompare(b.case_id);
    };

    return [...items].sort((a, b) => {
        switch (sort) {
            case 'created': {
                const diff = regDate(b).localeCompare(regDate(a));
                return diff !== 0 ? diff : b.case_id.localeCompare(a.case_id);
            }
            case 'updated': {
                const diff = (b.updated_at ?? '').localeCompare(a.updated_at ?? '');
                return diff !== 0 ? diff : b.case_id.localeCompare(a.case_id);
            }
            case 'priority': {
                if (a.priority !== b.priority) return a.priority - b.priority;
                return byDeadline(a, b);
            }
            case 'deadline':
            default:
                return byDeadline(a, b);
        }
    });
}

export interface CaseListView {
    items: CaseListItem[];
    total: number;
    page: number;
    limit: number;
    pages: number;
    isPending: boolean;
    isFetching: boolean;
    error: ApiError | null;
    /** Выборка пришла обрезанной лимитом сервера — показанному итогу верить нельзя. */
    truncated: boolean;
    /** Сколько дел под этим предикатом всего по данным сервера (до фильтров клиента). */
    serverTotal: number;
}

export function useCaseList(query: CaseQuery, enabled = true): CaseListView {
    const repeat = query.chip === 'repeat';

    const server = useCases(caseListParams(query), { enabled: enabled && !repeat });
    const repeats = useDashboardList(repeat ? { kpi: 'repeat' } : null, undefined, { enabled });

    if (repeat) {
        const loaded = repeats.data?.items ?? [];
        const serverTotal = repeats.data?.total ?? loaded.length;
        const matched = sortItems(
            loaded.filter((item) => matchesQuery(item, query)),
            query.sort,
        );
        const pages = Math.max(1, Math.ceil(matched.length / query.limit));
        const page = Math.min(Math.max(query.page, 1), pages);
        const start = (page - 1) * query.limit;
        return {
            items: matched.slice(start, start + query.limit),
            total: matched.length,
            page,
            limit: query.limit,
            pages,
            isPending: repeats.isPending,
            isFetching: repeats.isFetching,
            error: repeats.error ?? null,
            truncated: loaded.length < serverTotal,
            serverTotal,
        };
    }

    const total = server.data?.total ?? 0;
    return {
        items: server.data?.items ?? [],
        total,
        page: server.data?.page ?? query.page,
        limit: server.data?.limit ?? query.limit,
        pages: server.data?.pages ?? 1,
        isPending: server.isPending,
        isFetching: server.isFetching,
        error: server.error ?? null,
        truncated: false,
        serverTotal: total,
    };
}

export interface CasesTableProps {
    items: readonly CaseListItem[];
    variant: CaseRowVariant;
    onOpen: (caseId: string) => void;
    loading?: boolean;
    error?: Error | null;
    emptyText?: string;
}

export function CasesTable({
    items,
    variant,
    onOpen,
    loading = false,
    error = null,
    emptyText = 'Под фильтр и поиск ничего не подошло. Измените запрос или выберите «Все открытые» и сбросьте фильтры.',
}: CasesTableProps) {
    const head =
        variant === 'my' ? (
            <div className="my-row head">
                <span className="my-id">Дело</span>
                <span>Приоритет</span>
                <span className="my-title">Тема обращения</span>
                <span className="my-appl">Заявитель</span>
                <span className="my-date">Срок ответа</span>
                <span>Стадия</span>
                <span className="prog-wrap">Чек-лист</span>
            </div>
        ) : (
            <div className="list-row cases-row head">
                <span className="my-id">Дело</span>
                <span className="my-title">Тема обращения</span>
                <span className="my-appl">Категория</span>
                <span>Приоритет</span>
                <span>Стадия</span>
                <span>Исполнитель</span>
                <span className="my-date">Срок ответа</span>
                <span>Активность</span>
            </div>
        );

    let body: ReactNode;
    if (error) {
        body = <div className="detail-empty">{error.message || 'Не удалось загрузить дела.'}</div>;
    } else if (loading && items.length === 0) {
        body = <SkeletonRows label="Загрузка дел…" />;
    } else if (items.length === 0) {
        body = <div className="detail-empty">{emptyText}</div>;
    } else {
        body = items.map((item) => <CaseRow key={item.case_id} item={item} variant={variant} onOpen={onOpen} />);
    }

    return (
        <>
            {head}
            {body}
        </>
    );
}

export default CasesTable;
